import { useFormContext, useWatch } from 'react-hook-form'
import { useTranslation } from 'react-i18next'

import { FieldBooleanBadge } from '@/components/layout/field-row'
import { Switch } from '@/components/ui/switch'
import { Tooltip } from '@/components/ui/tooltip'
import { Building } from '@/reducers/types'

import { PlaceFeatureKey, PlaceFlags } from '../../features/places-features.constants'
import {
  applyFeatureChange,
  FeatureDisabledReason,
  getFeatureDefinition,
  getFeatureState,
  toFlags,
} from '../../features/places-features.rules'

interface UsePlaceFeatureControlParams {
  building: Building
  customLabel?: string
  error?: string
  featureKey: PlaceFeatureKey
  isAdmin: boolean
  isEditing: boolean
}

// Note: the label, hint and value control (On/Off badge, or a Switch while editing) of one feature
export const usePlaceFeatureControl = ({
  building,
  customLabel,
  error,
  featureKey,
  isAdmin,
  isEditing,
}: UsePlaceFeatureControlParams) => {
  const { t } = useTranslation(['organisation'])
  const { getValues, setValue } = useFormContext<PlaceFlags>()
  const values = useWatch<PlaceFlags>()
  const definition = getFeatureDefinition(featureKey)

  if (!definition) return null

  // Note: flags that live on another tab come from the stored building
  const flags: PlaceFlags = { ...toFlags(building), ...values }
  const state = getFeatureState(definition, flags, { isAdmin })
  const checked = !!values[featureKey]
  const label = customLabel ?? t(`organisation:places.building.toggles.${featureKey}`)
  const hint = definition.ghost
    ? t('organisation:places.features.ghostHint')
    : featureKey === 'isHoa'
      ? t('organisation:places.features.hints.isHoa')
      : undefined

  const getReasonText = (reason: FeatureDisabledReason) => {
    if (reason.type === 'admin') return t('organisation:places.features.reasons.admin')
    return t(`organisation:places.features.reasons.${reason.type}`, {
      feature: t(`organisation:places.building.toggles.${reason.key as PlaceFeatureKey}`),
    })
  }

  const handleChange = (nextChecked: boolean) => {
    const current = getValues()
    const next = applyFeatureChange(current, featureKey, nextChecked)

    ;(Object.keys(next) as PlaceFeatureKey[]).forEach((key) => {
      if (next[key] !== current[key]) {
        setValue(key, next[key], { shouldDirty: true, shouldTouch: true })
      }
    })
  }

  const reason = isEditing && state.reason ? getReasonText(state.reason) : undefined

  const control = isEditing ? (
    <Switch
      aria-invalid={!!error || undefined}
      checked={checked}
      disabled={state.disabled}
      name={featureKey}
      size="small"
      onChange={(event) => handleChange(event.target.checked)}
    />
  ) : (
    <FieldBooleanBadge value={checked} />
  )

  // Note: the reason a switch is locked shows on hover instead of taking up a line under the label
  const controlWithReason = reason ? <Tooltip trigger={control}>{reason}</Tooltip> : control

  return { checked, control, controlWithReason, hint, label }
}
